import { type UseMutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePerformGlobalRefresh } from "../components/header/hooks";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";
import type { ChangesSource } from "../utils/models";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { useInvalidateCallIns } from "./useInvalidateCallIns";

export interface CallOffShiftParams {
	departmentId: string;
	shiftId: string;
	nurseId: number;
	reason: string;
	removalMessage?: string;
	changeSource?: ChangesSource;
}

type CallOffShiftResponse = AxiosResponse<{ success: boolean; message: string }>;

type CallOffShiftOptions = Omit<UseMutationOptions<CallOffShiftResponse, Error, CallOffShiftParams, unknown>, "mutationFn">;

/** Re-reads everything a call-off changes. Shared so a batch refreshes exactly what a single call-off does — once. */
const useRefreshAfterCallOff = (): (() => Promise<void>) => {
	const queryClient = useQueryClient();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const invalidateCallIns = useInvalidateCallIns();

	return useCallback(async () => {
		await performGlobalRefresh({
			avoidRefreshFor: {
				requestsData: true,
				nursesData: true,
				notificationsData: true,
			},
		});
		await queryClient.invalidateQueries({ queryKey: ["departmentActionsMetadata"] });
		// Refresh the bedside nurse's own shift list so a self-call-off disappears immediately (web + mobile).
		await queryClient.invalidateQueries({ queryKey: ["userShifts"] });
		// …and the call-in reads, so the profile calendar marker and the mobile day list appear at once.
		await invalidateCallIns();
	}, [performGlobalRefresh, queryClient, invalidateCallIns]);
};

/**
 * Calls off one shift. The caller owns the success snackbar: an admin removing someone already says
 * "X was removed from …", and a second "called off" toast for the same action is noise (APP-1040).
 */
export const useCallOffShift = (options?: CallOffShiftOptions) => {
	const refreshAfterCallOff = useRefreshAfterCallOff();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	return useMutation({
		...options,
		mutationFn: (params: CallOffShiftParams) => backendService.callOffShift(params),
		onSuccess: async (data, variables, onMutateResult, context) => {
			await refreshAfterCallOff();
			await options?.onSuccess?.(data, variables, onMutateResult, context);
		},
		onError: async (e, variables, onMutateResult, context) => {
			showErrorSnackbar(t("error.call.off.failed"), { action: "call off failed" }, e);
			await options?.onError?.(e, variables, onMutateResult, context);
		},
	});
};

export type CallOffShiftFailure = { params: CallOffShiftParams; error: unknown };

/** Per-shift outcome of a batch, in request order, so the caller can name who was not called off. */
export type CallOffShiftsResult = {
	succeeded: CallOffShiftParams[];
	failed: CallOffShiftFailure[];
};

/**
 * Calls off N shifts: one `callOffShift` request per shift, SEQUENTIALLY (each call-off starts its own
 * replacement search, and firing them in parallel races the backend on the same day's coverage).
 *
 * One failure does not stop the rest — each shift is independent on the backend — and the promise resolves
 * with every outcome. The page is refreshed ONCE at the end when anything was called off; a failed refresh is
 * tracked, never turned into a failed batch (the call-offs are already saved). The caller owns the ONE
 * snackbar — success or the one naming who failed — because only it knows the names, and a batch that also
 * removed shifts without a call-off must report both in a single message. Each failure is also tracked here so
 * none goes unrecorded.
 */
export const useCallOffShifts = () => {
	const refreshAfterCallOff = useRefreshAfterCallOff();
	const showErrorSnackbar = useErrorSnackbar();
	const { trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (shifts: CallOffShiftParams[]): Promise<CallOffShiftsResult> => {
			if (shifts.length === 0) {
				throw new Error("useCallOffShifts called without any shifts to call off");
			}
			const result: CallOffShiftsResult = { succeeded: [], failed: [] };
			for (const params of shifts) {
				try {
					// Sequential on purpose — see the hook's doc comment.
					await backendService.callOffShift(params);
					result.succeeded.push(params);
				} catch (error) {
					trackError("call_off_shift_batch_item_error", {
						shift_id: params.shiftId,
						nurse_id: params.nurseId,
						department_id: params.departmentId,
						error_message: error instanceof Error ? error.message : String(error),
					});
					result.failed.push({ params, error });
				}
			}
			if (result.succeeded.length > 0) {
				try {
					await refreshAfterCallOff();
				} catch (error) {
					// The call-offs are saved: rejecting here would report them as failed and invite a retry that calls
					// the same nurses off twice. A stale page is recorded, and the next refresh catches it up.
					trackError("call_off_shift_batch_refresh_error", {
						shift_ids: result.succeeded.map((params) => params.shiftId).join(","),
						department_id: result.succeeded[0].departmentId,
						error_message: error instanceof Error ? error.message : String(error),
					});
				}
			}
			return result;
		},
		// Only reached when the batch itself breaks (empty input) — per-shift failures and a failed refresh resolve.
		onError: (e) => {
			showErrorSnackbar(t("error.call.off.failed"), { action: "call off batch failed" }, e);
		},
	});
};
